import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import {
  getInvoices,
  getInvoiceStats,
  updateInvoice,
  deleteInvoice,
} from '../../api/adminApi';
import { getAllExams } from '../../api/examApi';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';

const InvoicesManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [exams, setExams] = useState([]);
  const [viewDialog, setViewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const { showToast } = useToast();

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (search) params.search = search;
      if (selectedProduct) params.productId = selectedProduct._id;

      const { data } = await getInvoices(params);
      setInvoices(data.invoices || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      showToast('Failed to fetch invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (selectedProduct) params.productId = selectedProduct._id;
      const { data } = await getInvoiceStats(params);
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    getAllExams().then(({ data }) => setExams((data || []).filter(e => e.examType !== 'free-practice'))).catch(() => {});
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [page, pageSize, selectedProduct]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchInvoices();
  };

  const handleUpdateInvoice = async () => {
    try {
      await updateInvoice(editDialog._id, {
        price: Number(editDialog.price),
        discount: Number(editDialog.discount),
        amount: Number(editDialog.amount),
        currency: editDialog.currency,
        paymentMethod: editDialog.paymentMethod,
      });
      showToast('Invoice updated successfully', 'success');
      setEditDialog(null);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      showToast('Failed to update invoice', 'error');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await deleteInvoice(invoiceId);
      showToast('Invoice deleted successfully', 'success');
      setConfirmDialog(null);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      showToast('Failed to delete invoice', 'error');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatCurrency = (amount, currency) => {
    return `${amount?.toFixed(2) || '0.00'} ${currency || 'USD'}`;
  };

  const columns = [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice #',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          #{params.row.invoiceNumber}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
            {params.row.userId?.firstName?.[0] || params.row.user?.firstName?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.userId?.firstName || params.row.user?.firstName} {params.row.userId?.lastName || params.row.user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.userId?.email || params.row.user?.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'product',
      headerName: 'Product',
      width: 180,
      renderCell: (params) => {
        const registration = params.row.userProductRegistrationId;
        const productType = registration?.productType || 'N/A';
        const productName = registration?.productId?.name || registration?.productId?.label || 'N/A';
        return (
          <Box>
            <Chip
              label={productType}
              size="small"
              color={productType === 'Exam' ? 'primary' : productType === 'Speaking' ? 'secondary' : 'default'}
              sx={{ mb: 0.5 }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              {productName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      renderCell: (params) => formatCurrency(params.row.price, params.row.currency),
    },
    {
      field: 'discount',
      headerName: 'Discount',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" color={params.row.discount > 0 ? 'success.main' : 'text.secondary'}>
          {params.row.discount > 0 ? `-${formatCurrency(params.row.discount, params.row.currency)}` : '-'}
        </Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'Total',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="primary.main">
          {formatCurrency(params.row.amount, params.row.currency)}
        </Typography>
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Method',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.paymentMethod || 'N/A'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => setViewDialog(params.row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Invoice">
            <IconButton size="small" color="primary" onClick={() => setEditDialog({ ...params.row })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Invoice">
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDialog(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
              <ReceiptIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Invoices Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                View and manage payment invoices
              </Typography>
            </Box>
          </Box>
          <ModernButton
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchInvoices(); fetchStats(); }}
          >
            Refresh
          </ModernButton>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={selectedProduct ? 3 : 4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                {selectedProduct ? 'Invoices for Product' : 'Total Invoices'}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>{stats?.totalInvoices || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={selectedProduct ? 3 : 4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="success" />
              <Typography variant="subtitle2" color="text.secondary">
                {selectedProduct ? 'Product Revenue' : 'Total Revenue'}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600} color="success.main">
              {stats?.totalRevenue?.toFixed(2) || '0.00'}
            </Typography>
          </Card>
        </Grid>
        {selectedProduct && (
          <Grid item xs={12} sm={3}>
            <Card sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.light', backgroundColor: 'primary.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Selected Product</Typography>
              <Typography variant="body1" fontWeight={600} noWrap>{selectedProduct.name}</Typography>
              <Typography
                variant="caption"
                color="primary.main"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setSelectedProduct(null); setPage(1); }}
              >
                Clear filter
              </Typography>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sm={selectedProduct ? 3 : 4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>By Currency</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {stats?.currencyBreakdown?.map((c) => (
                <Chip
                  key={c._id}
                  label={`${c._id}: ${c.total?.toFixed(2)} (${c.count})`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filters */}
      <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <form onSubmit={handleSearch}>
              <TextField
                fullWidth
                placeholder="Search by user name, email, or invoice number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Grid>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={exams}
              getOptionLabel={(option) => option.name || 'Unnamed Exam'}
              value={selectedProduct}
              onChange={(_, newValue) => {
                setSelectedProduct(newValue);
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Exam / Product" placeholder="All products" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <ModernButton fullWidth variant="contained" onClick={() => { setPage(1); fetchInvoices(); }}>
              Search
            </ModernButton>
          </Grid>
        </Grid>
      </Card>

      {/* Data Grid */}
      <ModernDataGrid
        rows={invoices}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pagination
        page={page - 1}
        onPageChange={(newPage) => setPage(newPage + 1)}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(1);
        }}
        rowsPerPageOptions={[20, 50, 100]}
        rowCount={totalCount}
        paginationMode="server"
      />

      {/* View Dialog */}
      {viewDialog && (
        <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Invoice Details - #{viewDialog.invoiceNumber}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Customer Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Name:</strong> {viewDialog.userId?.firstName || viewDialog.user?.firstName} {viewDialog.userId?.lastName || viewDialog.user?.lastName}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {viewDialog.userId?.email || viewDialog.user?.email}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Product Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Type:</strong> {viewDialog.userProductRegistrationId?.productType || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Product:</strong> {viewDialog.userProductRegistrationId?.productId?.name || viewDialog.userProductRegistrationId?.productId?.label || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Payment Details
                    </Typography>
                    <Typography variant="body1">
                      <strong>Price:</strong> {formatCurrency(viewDialog.price, viewDialog.currency)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Discount:</strong> {viewDialog.discount > 0 ? formatCurrency(viewDialog.discount, viewDialog.currency) : 'None'}
                    </Typography>
                    <Typography variant="body1" color="primary.main" fontWeight={600}>
                      <strong>Total:</strong> {formatCurrency(viewDialog.amount, viewDialog.currency)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Method:</strong> {viewDialog.paymentMethod}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Timestamps
                    </Typography>
                    <Typography variant="body1">
                      <strong>Created:</strong> {formatDate(viewDialog.createdAt)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Updated:</strong> {formatDate(viewDialog.updatedAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Technical Details
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <strong>Invoice ID:</strong> {viewDialog._id}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <strong>Registration ID:</strong> {viewDialog.userProductRegistrationId?._id || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setViewDialog(null)}>Close</ModernButton>
            <ModernButton
              variant="contained"
              color="primary"
              onClick={() => {
                setViewDialog(null);
                setEditDialog({ ...viewDialog });
              }}
            >
              Edit Invoice
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Invoice - #{editDialog.invoiceNumber}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={editDialog.price || ''}
                  onChange={(e) => setEditDialog({ ...editDialog, price: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount"
                  type="number"
                  value={editDialog.discount || ''}
                  onChange={(e) => setEditDialog({ ...editDialog, discount: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  type="number"
                  value={editDialog.amount || ''}
                  onChange={(e) => setEditDialog({ ...editDialog, amount: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={editDialog.currency || ''}
                  onChange={(e) => setEditDialog({ ...editDialog, currency: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  value={editDialog.paymentMethod || ''}
                  onChange={(e) => setEditDialog({ ...editDialog, paymentMethod: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="Credit Card">Credit Card</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Stripe">Stripe</option>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" color="primary" onClick={handleUpdateInvoice}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDialog && (
        <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="sm">
          <DialogTitle>Delete Invoice</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </Alert>
            <Typography variant="body2">
              <strong>Invoice #:</strong> {confirmDialog.invoiceNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Amount:</strong> {formatCurrency(confirmDialog.amount, confirmDialog.currency)}
            </Typography>
            <Typography variant="body2">
              <strong>User:</strong> {confirmDialog.userId?.firstName || confirmDialog.user?.firstName} {confirmDialog.userId?.lastName || confirmDialog.user?.lastName}
            </Typography>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setConfirmDialog(null)}>Cancel</ModernButton>
            <ModernButton
              variant="contained"
              color="error"
              onClick={() => handleDeleteInvoice(confirmDialog._id)}
            >
              Delete Invoice
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InvoicesManagement;
